import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, Position, useReactFlow } from "reactflow";
import { toPng } from "html-to-image";
import download from "downloadjs";
import "reactflow/dist/style.css";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  ButtonGroup,
  CircularProgress,
  Fab,
  Icon,
  IconButton,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowUpward } from "@mui/icons-material";
import AddJobDialog from "components/AddJobDialog";
import MDBox from "components/MDBox";
import { addDepartment } from "services/api";
import DepartmentModel from "models/departmentModel";
import { addJob } from "services/api";
import JobModel from "models/jobModel";
import { deleteJob } from "services/api";
import { updateJobBranches } from "services/api";
import { getJobToBranch } from "services/api";
import { deleteDepartment } from "services/api";
import { getJobGroups } from "services/api";
import { addJobGroup } from "services/api";
import { deleteJobGroup } from "services/api";
import CustomJobNode from "./components/CustomJobNode";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { CloudDownload, CloudUpload, VisibilityOff } from "@mui/icons-material";
import { exportData } from "services/api";
import * as XLSX from "xlsx";
import { detectDataType } from "services/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jobFTEs } from "services/api";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import { getAllManpowerRequirements } from "services/api";
import { addManpowerRequirement } from "services/api";
import LoadingScreen from "components/Loading";
import ConfirmationDialog from "components/ConfirmationDialog";
import { deleteMultipleJobs } from "services/api";
import ImportButton from "components/ImportButton";
import { calculateOrgChartLayout } from "services/utils";
import { UnfoldMore, UnfoldLess, FilterAltOff } from "@mui/icons-material"; // Add these imports
import SignOffNode from "./components/SignOffNode";
import { useAuth } from "context/authContext";
import { calculateCSuite } from "services/utils";
import CustomOrgEdge from "./components/CustomOrgEdge";
import ExportJobButton from "./components/HandleJobExport";
import { ExportOrgChartPPTX } from "services/api";

const JobView = ({
  company,
  jobs,
  departments,
  setJobs,
  setDepartments,
  setJobToBranch,
  jobToBranch,
  branches,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { fitView, setCenter, getNodes } = useReactFlow();
  const reactFlowWrapper = useRef(null);
  // Dialog State
  const navigate = useNavigate();
  const [selectedJob, setSelectedJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [downloadLoadingJobs, setDownloadLoadingJobs] = useState(false);
  const [downloadLoadingDeps, setDownloadLoadingDeps] = useState(false);
  const [downloadLoadingOrgChart, setDownloadLoadingOrgChart] = useState(false);
  const [search, setSearch] = useState("");
  const snackbarRef = useRef();
  const [totalEmployees, setTotalEmployees] = useState();
  const [manpowerReq, setManpowerReq] = useState([]);
  const [manpowerReqDataRecieved, setManpowerReqDataRecieved] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [jobGroups, setJobGroups] = useState([]);
  const [ftes, setFtes] = useState([]);
  const [deleteMode, setDeleteMode] = useState(false);
  const [toDeleteJobs, setToDeleteJobs] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [deleteJobsOpen, setDeleteJobsOpen] = useState(false);
  const [deleteJobOpen, setDeleteJobOpen] = useState(false);
  const [jobIdToDelete, setJobIdToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmDepOpen, setConfirmDepOpen] = useState(false);
  const [depToDelete, setDepToDelete] = useState(null);
  // 1. STATE: Track currently visible jobs
  const [visibleJobs, setVisibleJobs] = useState([]);
  const [viewMode, setViewMode] = useState("interactive");
  // State
  const [isCSuiteMode, setIsCSuiteMode] = useState(false);
  const [rootJobId, setRootJobId] = useState(null);
  const { employee } = useAuth();
  const [maxDepth, setMaxDepth] = useState(1);
  const lastToggledIdRef = useRef(null);

  const handleSave = async (newData, jobId) => {
    const res = await addManpowerRequirement({ ...newData, jobId });
    if (res !== "error") {
      if (Array.isArray(manpowerReq)) {
        const exists = manpowerReq.some((item) => item.requirementId === res.requirementId);

        if (exists) {
          // Update the existing record in place
          setManpowerReq(
            manpowerReq.map((item) => (item.requirementId === res.requirementId ? res : item))
          );
        } else {
          // Add as a new record
          setManpowerReq([...manpowerReq, res]);
        }

        setManpowerReqDataRecieved(true);
      } else {
        setManpowerReq([res]);
      }
    }
  };

  const handleOpenDeleteDepartmentDialog = (depId) => {
    setDepToDelete(depId);
    setConfirmDepOpen(true);
  };

  const refreshJobGroups = async () => {
    const jobGroupsRes = await getJobGroups();
    if (jobGroupsRes !== "error") {
      setJobGroups(jobGroupsRes);
    }
  };

  const refreshFtes = async () => {
    const jobFTEsRes = await jobFTEs();
    if (jobFTEsRes !== "error") {
      const total = jobFTEsRes.reduce((sum, item) => sum + (item[1] || 0), 0);
      setTotalEmployees(total);
      setFtes(jobFTEsRes);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobGroupsRes, jobFTEsRes, manpowerReqRes] = await Promise.all([
          getJobGroups(),
          jobFTEs(),
          getAllManpowerRequirements(),
        ]);

        if (jobGroupsRes !== "error") {
          setJobGroups(jobGroupsRes);
        }

        if (jobFTEsRes !== "error") {
          const total = jobFTEsRes.reduce((sum, item) => sum + (item[1] || 0), 0);
          setTotalEmployees(total);
          setFtes(jobFTEsRes);
        }

        if (manpowerReqRes !== "error") {
          setManpowerReq(manpowerReqRes);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  // Helper: Trace parents to ensure the tree structure remains connected
  // Wrapped in useCallback so we can use it in useEffects
  const getJobsWithParents = useCallback(
    (targetJobs) => {
      if (!jobs) return [];
      const jobsToShow = new Set(targetJobs);

      const addParents = (job) => {
        if (job.reportingToSolidId) {
          const parent = jobs.find((p) => p.jobId === job.reportingToSolidId);
          if (parent && !jobsToShow.has(parent)) {
            jobsToShow.add(parent);
            addParents(parent); // Recursive call
          }
        }
      };

      targetJobs.forEach(addParents);
      return Array.from(jobsToShow);
    },
    [jobs]
  );

  // 2. HELPER: Filter Jobs by Depth (BFS Algorithm)
  const getJobsByDepth = useCallback(
    (depthLevel) => {
      if (!jobs || jobs.length === 0) return [];

      const allIds = new Set(jobs.map((j) => j.jobId));
      // Find Roots (CEO)
      const roots = jobs.filter((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));

      let result = [...roots];
      let currentLevelNodes = [...roots];

      // Loop for the number of levels requested (1 = N-1, 2 = N-2, etc.)
      for (let i = 0; i < depthLevel; i++) {
        const nextLevelNodes = [];

        currentLevelNodes.forEach((parent) => {
          // Find children of current level
          const children = jobs.filter((j) => j.reportingToSolidId === parent.jobId);
          nextLevelNodes.push(...children);
        });

        if (nextLevelNodes.length === 0) break;

        result = [...result, ...nextLevelNodes];
        currentLevelNodes = nextLevelNodes;
      }

      return result;
    },
    [jobs]
  );

  // Handler: Enter C-Suite Mode
  const handleEnterCSuite = () => {
    setIsCSuiteMode(true);
    // Default to the main root (e.g. Employee's own job or absolute CEO)
    // Assuming 'employee.jobId' is the starting point
    // Ensure we start at the top
    if (employee?.jobId) {
      setRootJobId(employee.jobId);
    } else {
      // Fallback: Find the absolute root of the organization
      const allIds = new Set(jobs.map((j) => j.jobId));
      const root = jobs.find((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));
      if (root) setRootJobId(root.jobId);
    }
  };

  // Handler: Exit C-Suite Mode
  const handleExitCSuite = () => {
    setIsCSuiteMode(false);
    setRootJobId(null);
    // Reset to normal visibility
    // ... your handleCollapseAll logic ...
  };

  // Handler: Drill Down (Clicking a node in C-Suite mode)
  const handleNodeClickCSuite = (e, nodeId) => {
    // Set this node as the new root
    setRootJobId(Number(nodeId));
  };

  // Handler: Go Up One Level
  const handleGoUp = () => {
    if (!rootJobId || !jobs) return;
    const current = jobs.find((j) => j.jobId === rootJobId);
    if (current && current.reportingToSolidId) {
      setRootJobId(current.reportingToSolidId);
    }
  };

  // Effect 2: Calculate Visible Nodes based on Filters (Search / Department / Default)
  useEffect(() => {
    if (!jobs || jobs.length === 0) return;

    // --- CASE A: SEARCH IS ACTIVE ---
    if (search) {
      const matches = jobs.filter((j) => j.jobName.toLowerCase().includes(search.toLowerCase()));
      setVisibleJobs(getJobsWithParents(matches));
    }

    // --- CASE B: DEPARTMENT IS SELECTED ---
    else if (selectedDepartment) {
      const matches = jobs.filter((j) => j.departmentId === selectedDepartment.departmentId);
      setVisibleJobs(getJobsWithParents(matches));
    }

    // --- CASE C: DEFAULT (Show Roots) ---
    else {
      // Only reset to roots if we haven't manipulated the tree yet
      // (or if we want to force reset on clear)
      const allIds = new Set(jobs.map((j) => j.jobId));
      const roots = jobs.filter((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));
      setVisibleJobs(roots);
    }
  }, [jobs, search, selectedDepartment, getJobsWithParents]); // Dependencies: Run whenever these change

  // Handle node click
  // const handleNodeClick = (_, node) => {
  //   setSelectedJob(jobs.find((job) => job.jobId === parseInt(node.id)));
  //   setOpen(true);
  // };

  const handleNodeClick = (e, node) => {
    // 1. C-Suite Mode (Drill Down) - HIGHEST PRIORITY
    // This allows navigation even if !canEdit (read-only users)
    if (isCSuiteMode) {
      setRootJobId(Number(node.id));
      return; // Stop here so the dialog doesn't open
    }

    // 2. Permission Check
    if (!canEdit) return;

    // 3. Delete Mode
    if (deleteMode) {
      handleSelectJobsTODelete(parseInt(node.id));
    }
    // 4. Standard Mode (Open Job Dialog)
    else {
      setSelectedJob(jobs?.find((job) => job.jobId === parseInt(node.id)));
      setOpen(true);
    }
  };

  const handleExpandAll = () => {
    if (selectedDepartment) {
      // If filtering by Dept, expand all jobs in that dept (plus parents)
      const deptJobs = jobs.filter((j) => j.departmentId === selectedDepartment.departmentId);
      setVisibleJobs(getJobsWithParents(deptJobs));
    } else {
      // Show every single job
      setVisibleJobs(jobs);
    }
  };

  const handleCollapseAll = () => {
    const allIds = new Set(jobs.map((j) => j.jobId));
    // Root = No manager OR Manager not in list
    const roots = jobs.filter((j) => !j.reportingToSolidId || !allIds.has(j.reportingToSolidId));
    setVisibleJobs(roots);
  };

  // 3. HANDLER: Toggle Logic
  const handleJobToggle = (event, jobId, isExpanded) => {
    event?.stopPropagation();

    lastToggledIdRef.current = jobId;

    if (isExpanded) {
      // --- COLLAPSE: Remove all descendants ---
      const getDescendants = (parentId) => {
        const children = jobs.filter((j) => j.reportingToSolidId === parentId);
        let descendants = [...children];
        children.forEach((child) => {
          descendants = [...descendants, ...getDescendants(child.jobId)];
        });
        return descendants;
      };

      const toRemove = getDescendants(jobId);
      const removeIds = new Set(toRemove.map((j) => j.jobId));

      setVisibleJobs((prev) => prev.filter((j) => !removeIds.has(j.jobId)));
    } else {
      // --- EXPAND: Add immediate children ---
      const children = jobs.filter((j) => j.reportingToSolidId === jobId);

      if (children.length > 0) {
        setVisibleJobs((prev) => {
          const currentIds = new Set(prev.map((j) => j.jobId));
          const newChildren = children.filter((c) => !currentIds.has(c.jobId));
          return [...prev, ...newChildren];
        });
      }
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setJobIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setJobIdToDelete(null);
  };

  // Close dialog
  const handleClose = () => {
    setOpen(false);
    setSelectedJob(null);
  };

  const handleSubmit = async (job, selectedBranches) => {
    job.companyId = company.companyId;
    const njob = await addJob(job);
    if (njob != "error") {
      const newJob = new JobModel(njob);
      newJob.reportingToSolidId = newJob.reportingToSolidId;
      setJobs((prevJobs) => {
        const jobIndex = prevJobs.findIndex((j) => j.jobId === newJob.jobId);

        if (jobIndex !== -1) {
          // Replace existing job
          const updatedJobs = [...prevJobs];
          updatedJobs[jobIndex] = newJob;
          return updatedJobs;
        } else {
          // Add new job
          return [...prevJobs, newJob];
        }
      });
    }
    const e = await updateJobBranches({
      jobId: njob.jobId,
      branches: selectedBranches,
    });
    if (e != "error") {
      const resu = await getJobToBranch();
      if (resu != "error") setJobToBranch(resu);
    }

    return njob;
  };

  const handleDelete = async (id) => {
    try {
      const jobsReporting = jobs ? jobs.filter((j) => j.reportingToSolidId == id) : null;

      if (jobsReporting.length > 0) {
        snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobError"), "error");
        return;
      }

      const res = await deleteJob(id);
      if (res == "success") {
        setJobs(jobs.filter((j) => j.jobId !== id));
      } else if (res[0] == "C") {
        snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobAssignedError"), "error");
        return;
      } else {
        snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
        return;
      }
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
    }
  };

  const handleAddKpi = () => {
    setSelectedJob(null);
    setOpen(true);
  };

  const handleSelectJobsTODelete = (jobId) => {
    setToDeleteJobs((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleDeleteSelectedJobs = async () => {
    if (toDeleteJobs.length === 0) return;

    try {
      // Call bulk delete API
      const res = await deleteMultipleJobs(toDeleteJobs);

      if (Array.isArray(res)) {
        if (res[0] === -1) {
          // Error from backend
          snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
        } else {
          // Success → res contains deleted IDs
          setJobs((prev) => prev.filter((j) => !res.includes(j.jobId)));
        }
      } else {
        snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
      }

      // Reset
      setToDeleteJobs([]);
      setDeleteMode(false);
    } catch (err) {
      snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
    }
  };

  const handleAddDepartment = async (depName) => {
    if (!depName.trim()) return;
    const newDepartment = new DepartmentModel({
      departmentName: depName,
      companyId: company.companyId,
    });
    addDepartment(newDepartment).then((dep) => {
      if (dep != "error") {
        setDepartments([...departments, new DepartmentModel(dep)]);
      }
    });
  };

  const handleEditDepartment = async (department) => {
    if (!department.departmentName.trim()) return;
    addDepartment(department).then((dep) => {
      if (dep !== "error") {
        // setDepartments((prevDepartments) =>
        //   prevDepartments.map((d) =>
        //     d.departmentId === dep.departmentId ? new DepartmentModel(dep) : d
        //   )
        // );
        setDepartments((prevDepartments) =>
          prevDepartments.map((d) =>
            d.departmentId === department.departmentId
              ? {
                  ...d,
                  departmentName: department.departmentName,
                }
              : d
          )
        );
      }
    });
  };

  const handleDeleteDepartment = async (depId) => {
    if (!depId) return;
    const isDepartmentUsed = jobs.some((job) => job.departmentId === depId);
    if (isDepartmentUsed) {
      snackbarRef.current?.showSnackbar(t("admin.jobs.depHasJobs"), "error");
      return; // stop here
    }

    try {
      const response = await deleteDepartment(depId); // Assuming this is your API call function
      if (response !== "error") {
        setDepartments(departments.filter((dep) => dep.departmentId !== depId));
        setDepartment(null); // Reset selected department if it's the deleted one
        setNewDepartmentDialogOpen(false); // Close the dialog
      }
    } catch (error) {}
  };

  const handleAddJobGroup = async (newJobGroup) => {
    addJobGroup(newJobGroup).then((group) => {
      if (group !== "error") {
        setJobGroups(
          (prev) =>
            newJobGroup?.jobGroupId == null
              ? [...prev, group] // If it's a new job group, add it to the list
              : prev.map((gr) => (gr.jobGroupId === newJobGroup?.jobGroupId ? group : gr)) // If editing, replace the existing one
        );
      }
    });
  };

  const handleDeleteJobGroup = async (jobGroupId) => {
    deleteJobGroup(jobGroupId).then((e) => {
      if (e != "error") {
        setJobGroups(jobGroups?.filter((jg) => jg.jobGroupId !== jobGroupId));
      }
    });
  };

  //IMPORT AND EXPORT
  const handleExport = async (type) => {
    if (type === "jobs") {
      setDownloadLoadingJobs(true);
    } else if (type === "departments") {
      setDownloadLoadingDeps(true);
    } else {
      setDownloadLoadingOrgChart(true);
    }

    if (type === "orgChartPptx") {
      await ExportOrgChartPPTX();
    } else {
      await exportData(type);
    }
    if (type === "jobs") {
      setDownloadLoadingJobs(false);
    } else if (type === "departments") {
      setDownloadLoadingDeps(false);
    } else {
      setDownloadLoadingOrgChart(false);
    }
  };

  const handleExportPng = () => {
    if (!reactFlowWrapper.current) return;

    toPng(reactFlowWrapper.current, {
      backgroundColor: "#ffffff",
      cacheBust: true,
      pixelRatio: 3, // 2–4 recommended for sharp results
      style: {
        transform: "scale(1)",
        transformOrigin: "top left",
        width: reactFlowWrapper.current.scrollWidth + "px",
        height: reactFlowWrapper.current.scrollHeight + "px",
      },
    })
      .then((dataUrl) => {
        download(dataUrl, "organization-chart.png");
      })
      .catch((err) => {
        console.error("Export failed:", err);
      });
  };

  // 4. UPDATE USEMEMO to Switch Node Types
  const { nodes, edges, nodeTypes, edgeTypes } = useMemo(() => {
    if (!visibleJobs || visibleJobs.length === 0) return { nodes: [], edges: [], nodeTypes: {} };

    // A. Determine Node Type
    const isSignOffMode = viewMode !== "interactive";
    const NodeType = isSignOffMode ? "signOffNode" : "customNode";

    // 1. Determine Settings based on Mode
    let jobsToShow = [];
    let layoutOptions = {};

    // --- C-SUITE MODE ---
    if (isCSuiteMode && rootJobId) {
      layoutOptions = {
        rootId: rootJobId,
        isJob: true,
        direction: "TB",
        vSpacing: 120,
        hSpacing: 30,
        maxDepth: maxDepth, // <--- KEY FIX: Show only immediate team
      };
      // Pass full dataset so utility can find children
      jobsToShow = jobs;
    }

    // B. Calculate Layout
    const {
      nodeMap,
      edges: calculatedEdges,
      processedJobs,
    } = isCSuiteMode
      ? calculateCSuite(jobsToShow, layoutOptions)
      : calculateOrgChartLayout(visibleJobs, {
          vSpacing: 150,
          hSpacing: 50,
          isJob: true,
        });

    // C. Build Nodes
    const nodes = isCSuiteMode
      ? processedJobs.map((job) => {
          const layoutData = nodeMap.get(job.jobId?.toString()) ?? { x: 0, y: 0, width: 180 };
          const isSignOff = isCSuiteMode;

          return {
            id: job.jobId.toString(),
            type: isSignOff ? "signOffNode" : "customNode",

            // Dynamic Handles
            sourcePosition: isSignOff ? Position.Right : Position.Bottom,
            targetPosition: isSignOff ? Position.Left : Position.Top,

            data: {
              label: job.jobName,
              // Hook up drill down
              onClick: isSignOff ? (e) => handleNodeClickCSuite(e, job.jobId) : null,
              // ... rest of data ...
              jobGroup: jobGroups
                .filter((g) => g.jobId === job.jobId)
                .map((g) => g.jobGroup)
                .join(", "),
              fte: ftes?.find(([id]) => id === job.jobId)?.[1] ?? null,
              targetPosition: Position.Top,
              sourcePosition: Position.Bottom,
            },
            position: { x: layoutData.x, y: layoutData.y },
            style: {
              width: `${layoutData.width}px`,
              height: "auto",
              // Remove border from wrapper in C-Suite mode to let Card handle it
              border: "none",
              background: "transparent",
            },
          };
        })
      : visibleJobs.map((job) => {
          const isMarkedForDelete = deleteMode && toDeleteJobs.includes(job.jobId);
          const manp = manpowerReq?.find((req) => req?.jobId === job?.jobId);
          const layoutData = nodeMap.get(job.jobId) ?? { x: 0, y: 0, width: 180 };

          // Node Type Logic
          const isSignOff = isCSuiteMode;

          // Toggle logic only needed for Interactive mode
          const hasChildrenInDB = jobs?.some((j) => j.reportingToSolidId === job.jobId);
          const isExpanded = visibleJobs.some((j) => j.reportingToSolidId === job.jobId);

          return {
            id: job.jobId.toString(),
            type: isSignOff ? "signOffNode" : "customNode", // <--- Dynamic Type
            sourcePosition: isCSuiteMode ? Position.Right : Position.Bottom,
            targetPosition: isCSuiteMode ? Position.Left : Position.Top,

            data: isSignOff
              ? {
                  label: job.jobName,
                  // If C-Suite, clicking drills down. If Normal, it might open dialog.
                  // Note: SignOffNode doesn't usually have click handlers on the card itself,
                  // you might need to add an onClick to the SignOffNode root div or pass it here.
                  onClick: isCSuiteMode ? (e) => handleNodeClickCSuite(e, job.jobId) : null,

                  // ... rest of data props (jobGroup, fte, etc) ...
                  jobGroup: jobGroups
                    .filter((g) => g.jobId === job.jobId)
                    .map((g) => g.jobGroup)
                    .join(", "),
                  fte: ftes?.find(([id]) => id === job.jobId)?.[1] ?? null,
                  id: job.jobId,
                  isExpanded,
                  isLeaf: !hasChildrenInDB,
                  onToggle: handleJobToggle,
                  manpowerReq: manpowerReqDataRecieved && manp,
                  manpowerReqDataRecieved: manpowerReqDataRecieved,
                  handleSave: (newData) => handleSave(newData, job.jobId),
                  totalEmployees: totalEmployees,
                  disabled: !canEdit,
                  job: {
                    jobId: job.jobId,
                    jobName: job.jobName,
                    reportingToSolidId: job.reportingToSolidId,
                    ...manp,
                  },
                  isScenario: false,
                  loc: "/organization",
                  refreshFtes: refreshFtes,
                }
              : {
                  label: job.jobName,
                  // Shared Data
                  jobGroup: jobGroups
                    .filter((g) => g?.jobId === job?.jobId)
                    .map((g) => g.jobGroup)
                    .join(", "),
                  fte: ftes?.find(([id]) => id === job.jobId)?.[1] ?? null,

                  // Interactive Data (Only passed if needed, but harmless to pass always)
                  id: job.jobId,
                  isExpanded,
                  isLeaf: !hasChildrenInDB,
                  onToggle: handleJobToggle,
                  manpowerReq: manpowerReqDataRecieved && manp,
                  manpowerReqDataRecieved: manpowerReqDataRecieved,
                  handleSave: (newData) => handleSave(newData, job.jobId),
                  totalEmployees: totalEmployees,
                  disabled: !canEdit,
                  job: {
                    jobId: job.jobId,
                    jobName: job.jobName,
                    reportingToSolidId: job.reportingToSolidId,
                    ...manp,
                  },
                  isScenario: false,
                  loc: "/organization",
                  refreshFtes: refreshFtes,
                },
            position: { x: layoutData.x, y: layoutData.y },
            width: layoutData.width,
            style:
              viewMode === "interactive"
                ? {
                    width: `${layoutData.width}px`,
                    height: "auto",
                  }
                : {
                    cursor: "pointer",
                    border: isMarkedForDelete ? "2px solid red" : "1px solid #ccc",
                    backgroundColor: "#fff",
                    // 4. Apply dynamic width
                    width: `${layoutData.width}px`,
                    height: "auto",
                    borderRadius: "8px",
                    minHeight: "40px",
                  },
          };
        });

    const childCountMap = {};
    calculatedEdges.forEach((edge) => {
      childCountMap[edge.source] = (childCountMap[edge.source] || 0) + 1;
    });
    // 5. Build Edges (Override the edges returned by utility to add custom data)
    const edges = calculatedEdges.map((edge) => {
      // Find the target node (The child)
      const targetJob = processedJobs.find((j) => j.jobId.toString() === edge.target);

      return {
        ...edge,
        type: "customOrgEdge", // Ensure this is set
        data: {
          // Pass the column width we calculated
          // Fallback to 180 if undefined
          maxColumnWidth: targetJob?._layoutData?.colWidth || 180,
          childCount: childCountMap[edge.source] || 0,
        },
      };
    });

    // D. Define Node Types Map
    const nodeTypes = {
      customNode: CustomJobNode,
      signOffNode: SignOffNode, // Register new node
    };

    const edgeTypes = {
      customOrgEdge: CustomOrgEdge,
    };

    return { nodes, edges, nodeTypes, edgeTypes };
  }, [
    visibleJobs,
    viewMode,
    jobs,
    jobGroups,
    ftes,
    manpowerReq,
    toDeleteJobs,
    deleteMode,
    manpowerReqDataRecieved,
    isCSuiteMode,
    rootJobId,
    maxDepth,
  ]);

  useEffect(() => {
    // Wait a tiny bit for the nodes to be rendered in the DOM
    const timer = setTimeout(() => {
      // Safety check
      if (nodes.length === 0) return;

      // PRIORITY 1: Handle User Toggle (Expand/Collapse)
      // We check the Ref. If it has a value, it means the user just clicked something.
      if (lastToggledIdRef.current !== null) {
        const targetNode = nodes.find((n) => n.id === lastToggledIdRef.toString());

        if (targetNode) {
          const x = targetNode.position.x + targetNode.width / 2;
          const y = targetNode.position.y + 100; // Offset slightly down to see children

          setCenter(x, y, { zoom: 1, duration: 800 });
        }

        // 👇 CRITICAL: Clear the ref AFTER we have successfully centered
        lastToggledIdRef.current = null;
        return;
      }

      // PRIORITY 2: Search Result
      if (search) {
        const match = nodes.find((n) => n.data.label.toLowerCase().includes(search.toLowerCase()));
        if (match) {
          setCenter(match.position.x + match.width / 2, match.position.y + 50, {
            zoom: 1.2,
            duration: 800,
          });
          return;
        }
      }

      // PRIORITY 3: Default (Fit View)
      // Only runs if no Toggle and no Search happened
      fitView({
        padding: 0.2,
        duration: 800,
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [nodes, search, fitView, setCenter]); // 👈 Removed lastToggledId from dependencies

  // Helper to calculate max depth of the current subtree
  const maxPossibleDepth = useMemo(() => {
    if (!rootJobId || !jobs) return 1;

    const getDepth = (id) => {
      const children = jobs.filter((j) => j.reportingToSolidId === id);
      if (children.length === 0) return 0; // No children = 0 levels down
      // Return 1 + the deepest child path
      return 1 + Math.max(...children.map((c) => getDepth(c.jobId)));
    };

    return getDepth(rootJobId);
  }, [rootJobId, jobs]);

  const handleDepthChange = (e) => {
    let val = parseInt(e.target.value, 10);

    // Validation constraints
    if (isNaN(val) || val < 1) val = 1; // Minimum 1 level
    if (val > maxPossibleDepth) val = maxPossibleDepth; // Clamp to max tree height

    setMaxDepth(val);
  };

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  return (
    <Box style={{ height: "75vh", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <MDTypography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {t("admin.jobs.numberOfJobs", { count: jobs?.length })}
        </MDTypography>

        {/* Export + Import + Delete Single */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
          <ExportJobButton
            handleExport={handleExport}
            loadingStates={{
              jobs: downloadLoadingJobs,
              departments: downloadLoadingDeps,
              orgChartPptx: downloadLoadingOrgChart,
            }}
          />

          <ImportButton
            // Only allow Jobs to be imported from this page
            allowedTypes={["jobs", "departments", "jobDetails"]}
            // Pass context needed for the ImportReview page (optional now, mostly companyId)
            extraState={{ companyId: company.companyId, departments }}
            // Handle UI feedback
            onError={handleImportError}
            disabled={!canEdit}
          />

          {/* Delete Confirmations */}
          <ConfirmationDialog
            open={deleteJobsOpen}
            onClose={() => setDeleteJobsOpen(false)}
            onConfirm={async () => {
              try {
                setLoadingDelete(true);
                await handleDeleteSelectedJobs();
              } catch (error) {
                snackbarRef.current?.showSnackbar(t("admin.jobs.deleteJobGenericError"), "error");
              } finally {
                setLoadingDelete(false);
                setDeleteJobsOpen(false);
              }
            }}
            loading={loadingDelete}
            message={`Are you sure you want to delete ${toDeleteJobs.length} job(s)?`}
          />

          <ConfirmationDialog
            open={deleteDialogOpen}
            onClose={handleCloseDeleteDialog}
            onConfirm={async () => {
              await handleDelete(jobIdToDelete);
              handleCloseDeleteDialog();
            }}
            message={`Are you sure you want to delete this job?`}
          />
        </Stack>
      </Box>

      {/* Toolbar: Search, Filter, Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap", // Allows wrapping on small screens
        }}
      >
        {/* 1. Search Input (Smaller width) */}
        {!isCSuiteMode && (
          <TextField
            label={t("admin.jobs.searchNodes")}
            variant="outlined"
            size="small" // Makes it more compact
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: "100%", md: 220 } }} // Fixed width on desktop
          />
        )}

        {/* 2. Department Filter (Autocomplete) */}
        {!isCSuiteMode && (
          <Autocomplete
            options={departments || []}
            getOptionLabel={(option) => option.departmentName || ""}
            value={selectedDepartment} // You need to create this state
            onChange={(_, newValue) => setSelectedDepartment(newValue)} // You need to create this handler
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.jobs.depFilter")}
                size="small"
                variant="outlined"
              />
            )}
            sx={{ width: { xs: "100%", md: 250 } }}
          />
        )}

        {/* 3. Expand / Collapse All Toggles */}
        {!isCSuiteMode && (
          <Box sx={{ display: "flex", gap: 1, borderLeft: "1px solid #ccc", pl: 2 }}>
            <Tooltip title={t("admin.jobs.expand")}>
              <IconButton onClick={handleExpandAll} color="primary" size="small">
                <UnfoldMore />
              </IconButton>
            </Tooltip>

            <Tooltip title={t("admin.jobs.collapse")}>
              <IconButton onClick={handleCollapseAll} color="primary" size="small">
                <UnfoldLess />
              </IconButton>
            </Tooltip>

            {/* Optional: Reset Filter Button */}
            {(search || selectedDepartment) && (
              <Tooltip title="Reset Filters">
                <IconButton
                  onClick={() => {
                    setSearch("");
                    setSelectedDepartment(null);
                  }}
                  color="error"
                  size="small"
                >
                  <FilterAltOff />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Toolbar */}
        {employee.role === 1 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Standard Mode Controls ... */}

            {/* C-Suite Toggle */}
            {!isCSuiteMode ? (
              <MDButton variant="outlined" color="primary" onClick={handleEnterCSuite}>
                {t("admin.jobs.cSuite")}
              </MDButton>
            ) : (
              // C-Suite Navigation Controls
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  bgcolor: "#f0f8ff",
                  p: 0.5,
                  borderRadius: 2,
                }}
              >
                <Tooltip title={t("admin.jobs.goUpLvl")}>
                  <span>
                    <IconButton
                      onClick={handleGoUp}
                      size="small"
                      disabled={rootJobId === employee?.jobId} // Disable if at top
                    >
                      <ArrowUpward />
                    </IconButton>
                  </span>
                </Tooltip>
                {/* 👇 LEVEL CONTROL INPUT 👇 */}
                <Tooltip title={`${t("admin.jobs.maxLvls")} ${maxPossibleDepth}`}>
                  <TextField
                    type="number"
                    size="small"
                    value={maxDepth}
                    onChange={handleDepthChange}
                    label={t("admin.jobs.lvls")}
                    variant="outlined"
                    InputProps={{
                      inputProps: {
                        min: 1,
                        max: maxPossibleDepth,
                        style: { padding: "4px 8px", textAlign: "center" },
                      },
                    }}
                    sx={{
                      width: "80px",
                      bgcolor: "white",
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": { paddingRight: 0 },
                    }}
                  />
                </Tooltip>

                <Typography variant="caption" fontWeight="bold" color="primary">
                  {t("admin.jobs.drillDown")}
                </Typography>

                <MDButton variant="contained" color="error" size="small" onClick={handleExitCSuite}>
                  {t("admin.jobs.exit")}
                </MDButton>
              </Box>
            )}
          </Box>
        )}

        {/* Spacer to push Delete buttons to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* 4. Delete Multiple Buttons */}
        {!isCSuiteMode && (
          <MDButton
            variant="outlined"
            color={deleteMode ? "secondary" : "error"}
            disabled={!canEdit}
            onClick={() => setDeleteMode(!deleteMode)}
            sx={{ height: 40 }} // Matched size='small' height roughly
          >
            {deleteMode
              ? t("admin.employees.delete.cancel")
              : t("admin.employees.delete.deleteMultiple")}
          </MDButton>
        )}

        {deleteMode && toDeleteJobs.length > 0 && (
          <MDButton color="error" onClick={() => setDeleteJobsOpen(true)} sx={{ height: 40 }}>
            {t("admin.employees.delete.deleteSelected")} ({toDeleteJobs.length})
          </MDButton>
        )}
      </Box>

      <div ref={reactFlowWrapper} style={{ height: "75vh", width: "100%" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          edgeTypes={isCSuiteMode && edgeTypes}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
          minZoom={0.1}
        >
          {!isCSuiteMode && <Background color="#aaa" gap={16} />}
          {!isCSuiteMode && <MiniMap />}
          {!isCSuiteMode && <Controls />}
        </ReactFlow>
      </div>

      {canEdit && <AddFloatingButton onClick={handleAddKpi} />}

      <AddJobDialog
        open={open}
        onClose={handleClose}
        job={selectedJob}
        jobs={jobs}
        departments={departments}
        handleSubmit={handleSubmit}
        handleDelete={handleDelete}
        handleOpenDeleteDialog={handleOpenDeleteDialog}
        dep={departments?.find((d) => d.departmentId === selectedJob?.departmentId)}
        handleAddDepartment={handleAddDepartment}
        handleDeleteDepartment={handleOpenDeleteDepartmentDialog}
        handleEditDepartment={handleEditDepartment}
        jobToBranch={jobToBranch}
        branches={branches}
        handleAddJobGroup={handleAddJobGroup}
        handleDeleteJobGroup={handleDeleteJobGroup}
        jobGroups={jobGroups?.filter((j) => j.jobId === selectedJob?.jobId)}
        refreshJobGroups={refreshJobGroups}
      />
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
      <ConfirmationDialog
        open={confirmDepOpen}
        onClose={() => setConfirmDepOpen(false)}
        message="Are you sure you want to delete this department?"
        onConfirm={async () => {
          if (depToDelete) {
            await handleDeleteDepartment(depToDelete);
          }
          setConfirmDepOpen(false);
          setDepToDelete(null);
        }}
      />
    </Box>
  );
};

JobView.propTypes = {
  company: PropTypes.object.isRequired,
  jobs: PropTypes.array.isRequired,
  departments: PropTypes.array.isRequired,
  setJobs: PropTypes.func.isRequired,
  setDepartments: PropTypes.func.isRequired,
  setJobToBranch: PropTypes.func.isRequired,
  jobToBranch: PropTypes.array.isRequired,
  branches: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default JobView;
